import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { useTranslation, LANGUAGE_OPTIONS } from '../translations';
import { api } from '../services/api';
import { formatPickup } from '../utils/datetime';
import { BookingDetailModal } from '../components/driver/BookingDetailModal';
import { Helmet } from 'react-helmet';
import { RegisterScreen, DriverSetupScreen, VehicleManageScreen, DocumentsScreen } from './DriverScreens';
import { requestNotificationPermission, onForegroundMessage } from '../firebase-config';

// Notification sound using Web Audio API
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    // Two-tone chime
    [523.25, 659.25].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.3, now + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.15 + 0.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.15);
      osc.stop(now + i * 0.15 + 0.4);
    });
    // Cleanup
    setTimeout(() => ctx.close(), 1500);
  } catch (e) {
    console.warn('Audio not available:', e);
  }
}

// --- PWA Install Prompt ---
function useInstallPrompt() {
  const [prompt, setPrompt] = useState(null);
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  const install = async () => { if (prompt) { prompt.prompt(); setPrompt(null); } };
  return { canInstall: !!prompt, install };
}

// --- Icons (inline SVG) ---
const Icons = {
  Home: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  List: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  Tag: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  Car: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6"><path d="M14 16H9m10 0h3v-3.15a1 1 0 00-.84-.99L16 11l-2.7-3.6a1 1 0 00-.8-.4H5.24a1 1 0 00-.93.67L3 11l-1 .5V16h3m0 0a2 2 0 104 0m-4 0a2 2 0 114 0m8 0a2 2 0 104 0m-4 0a2 2 0 114 0"/></svg>,
  User: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  MapPin: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  Clock: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Users: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
  ChevronRight: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><polyline points="9 18 15 12 9 6"/></svg>,
  ChevronLeft: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><polyline points="15 18 9 12 15 6"/></svg>,
  Star: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-yellow-400"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  Check: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><polyline points="20 6 9 17 4 12"/></svg>,
  X: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Camera: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  Upload: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/></svg>,
  Luggage: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><rect x="6" y="7" width="12" height="14" rx="2"/><path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2"/></svg>,
  Globe: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>,
  Refresh: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>,
  Download: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  LogOut: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  Euro: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><path d="M17 4a8 8 0 00-7.48 10.79M17 20a8 8 0 01-7.48-10.79M4 10h10M4 14h10"/></svg>,
};

// --- Login Screen ---
function LoginScreen({ t, onLogin, onSwitchToRegister, language, setLanguage }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLang, setShowLang] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [code2FA, setCode2FA] = useState('');
  const [twoFAEmail, setTwoFAEmail] = useState('');
  const { verify2FA } = useAuthStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await onLogin(email, password);
      if (result?.requires_2fa) {
        setTwoFAEmail(result.email || email);
        setShow2FA(true);
      }
    }
    catch { setError(t('auth_invalid')); }
    finally { setLoading(false); }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try { await verify2FA(twoFAEmail, code2FA); }
    catch { setError('Code ungültig'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="pwa-login-screen">
      <div className="absolute top-4 right-4 z-10">
        <button onClick={() => setShowLang(!showLang)} className="text-slate-400 p-2" data-testid="pwa-lang-btn">
          <Icons.Globe />
        </button>
        {showLang && (
          <div className="absolute right-0 mt-1 bg-slate-800 rounded-lg shadow-xl border border-slate-700 py-1 max-h-64 overflow-y-auto w-40 z-50">
            {LANGUAGE_OPTIONS.map((l) => (
              <button key={l.code} onClick={() => { setLanguage(l.code); setShowLang(false); }}
                className={`w-full text-left px-3 py-2 text-sm ${language === l.code ? 'text-orange-400 bg-slate-700' : 'text-slate-300 hover:bg-slate-700'}`}>
                {l.flag} {l.name}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex-1 flex flex-col justify-center px-6 pb-12">
        <div className="text-center mb-10">
          <div className="text-5xl font-black text-orange-500 mb-1">366</div>
          <div className="text-lg font-semibold text-white tracking-wide">DRIVER</div>
          <p className="text-slate-400 text-sm mt-3">{t('driver_portal')}</p>
        </div>
        {show2FA ? (
          <form onSubmit={handle2FA} className="space-y-4 max-w-sm mx-auto w-full">
            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-lg text-center">{error}</div>}
            <p className="text-slate-400 text-sm text-center">2FA-Code eingeben</p>
            <input type="text" value={code2FA} onChange={(e) => setCode2FA(e.target.value)} placeholder="123456" maxLength={6}
              className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3.5 text-center text-2xl tracking-widest focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none placeholder:text-slate-500" data-testid="pwa-2fa-code" autoFocus required />
            <button type="submit" disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-semibold py-3.5 rounded-xl text-base transition-colors disabled:opacity-50" data-testid="pwa-2fa-submit">
              {loading ? t('common_loading') : t('common_confirm')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 max-w-sm mx-auto w-full">
            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-lg text-center" data-testid="pwa-login-error">{error}</div>}
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('auth_email')}
              className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3.5 text-base focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none placeholder:text-slate-500" data-testid="pwa-login-email" required />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('auth_password')}
              className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3.5 text-base focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none placeholder:text-slate-500" data-testid="pwa-login-password" required />
            <button type="submit" disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-semibold py-3.5 rounded-xl text-base transition-colors disabled:opacity-50" data-testid="pwa-login-submit">
              {loading ? t('common_loading') : t('auth_login_btn')}
            </button>
          </form>
        )}
        <button onClick={onSwitchToRegister} className="text-orange-400 text-sm text-center mt-5" data-testid="pwa-switch-register">{t('auth_switch_to_register')}</button>
        <p className="text-center text-slate-500 text-xs mt-4">366Transfer.com GmbH</p>
      </div>
    </div>
  );
}

// --- Dashboard Screen ---
function DashboardScreen({ t, driverProfile, userName, onRefresh, onNavigate, notifStatus, onRetryNotif }) {
  const stats = [
    { label: t('driver_new_requests'), value: '--', color: 'bg-blue-500/10 text-blue-400', tab: 'requests' },
    { label: t('driver_active_offers_count'), value: '--', color: 'bg-orange-500/10 text-orange-400', tab: 'offers' },
    { label: t('driver_bookings_count'), value: '--', color: 'bg-emerald-500/10 text-emerald-400', tab: 'bookings' },
  ];
  const [counts, setCounts] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [req, off, book] = await Promise.all([
          api.get('/driver/requests'), api.get('/driver/my-offers'), api.get('/driver/bookings')
        ]);
        setCounts({ requests: req.data?.length || 0, offers: off.data?.length || 0, bookings: book.data?.length || 0 });
      } catch {}
    })();
  }, []);

  return (
    <div className="pb-4" data-testid="pwa-dashboard-screen">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-5 mb-5 border border-slate-700/50">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400 font-bold text-lg">
            {userName?.[0] || 'F'}
          </div>
          <div>
            <h2 className="text-white font-semibold text-lg" data-testid="pwa-driver-name">{userName || 'Fahrer'}</h2>
            <div className="flex items-center gap-1.5 text-sm text-slate-400">
              <Icons.Star /><span>{driverProfile?.rating?.toFixed(1) || '5.0'}</span>
              <span className="mx-1">|</span>
              <span>{driverProfile?.total_trips || 0} {t('profile_trips')}</span>
            </div>
          </div>
        </div>
        {driverProfile?.status === 'pending' && (
          <div className="mt-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs p-2.5 rounded-lg">{t('driver_pending_desc')}</div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        {stats.map((s, i) => (
          <button key={i} onClick={() => onNavigate(s.tab)} className={`${s.color} rounded-xl p-3 text-center active:scale-95 transition-transform`} data-testid={`pwa-stat-${s.tab}`}>
            <div className="text-2xl font-bold">{counts[s.tab] ?? s.value}</div>
            <div className="text-xs mt-1 opacity-80">{s.label}</div>
          </button>
        ))}
      </div>

      <button onClick={onRefresh} className="w-full flex items-center justify-center gap-2 text-slate-400 hover:text-white py-3 rounded-xl border border-slate-700/50 active:bg-slate-800 transition-colors" data-testid="pwa-refresh-btn">
        <Icons.Refresh /><span className="text-sm">{t('common_loading').replace('...', '')}</span>
      </button>

      {/* Notification Status */}
      {notifStatus === 'subscribed' && (
        <div className="mt-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-center gap-2" data-testid="pwa-notif-active">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-emerald-400 shrink-0"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
          <span className="text-xs text-emerald-400">Push-Benachrichtigungen aktiv</span>
        </div>
      )}
      {notifStatus === 'denied' && (
        <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-center gap-2" data-testid="pwa-notif-denied">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-red-400 shrink-0"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
          <span className="text-xs text-red-400">Benachrichtigungen blockiert. Bitte in Browser-Einstellungen erlauben.</span>
        </div>
      )}
      {(notifStatus === 'error' || notifStatus === 'idle') && notifStatus !== 'subscribing' && (
        <button onClick={onRetryNotif} className="mt-3 w-full bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 flex items-center gap-2 active:bg-orange-500/20" data-testid="pwa-notif-retry">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-orange-400 shrink-0"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
          <span className="text-xs text-orange-400">Push-Benachrichtigungen aktivieren</span>
        </button>
      )}
      {notifStatus === 'subscribing' && (
        <div className="mt-3 bg-slate-800/60 border border-slate-700/50 rounded-xl p-3 flex items-center gap-2" data-testid="pwa-notif-loading">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-slate-400 shrink-0 animate-pulse"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
          <span className="text-xs text-slate-400">Benachrichtigungen werden eingerichtet...</span>
        </div>
      )}
    </div>
  );
}

// --- Requests Screen ---
function RequestsScreen({ t, onSelectRequest, refreshTrigger }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/driver/requests'); setRequests(r.data || []); }
    catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshTrigger > 0) load(); }, [refreshTrigger, load]);

  const formatDate = (d) => formatPickup(d);

  if (loading) return <div className="text-center py-16 text-slate-400" data-testid="pwa-requests-loading"><div className="w-8 h-8 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-3" />{t('common_loading')}</div>;

  return (
    <div className="pb-4" data-testid="pwa-requests-screen">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-white font-bold text-xl tracking-tight">{t('driver_tab_new')}</h2>
          <p className="text-slate-500 text-xs mt-0.5">{requests.length} verfügbar</p>
        </div>
        <button onClick={load} className="w-10 h-10 flex items-center justify-center text-slate-400 active:text-white active:bg-white/5 rounded-xl transition-all"><Icons.Refresh /></button>
      </div>
      {requests.length === 0 ? (
        <div className="text-center py-20" data-testid="pwa-no-requests">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-800/60 flex items-center justify-center"><Icons.List /></div>
          <p className="text-slate-500">{t('driver_no_requests')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <button key={r.id} onClick={() => onSelectRequest(r)} className="w-full bg-slate-900 border border-white/10 p-5 text-left active:scale-[0.98] active:border-orange-500/30 transition-all" data-testid={`pwa-request-${r.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-1.5 text-xs text-slate-400"><Icons.Clock />{formatDate(r.pickup_date)}</div>
                <div className="flex items-center gap-2">
                  {r.preferred_vehicle_type && <span className="text-[10px] bg-orange-500/10 text-orange-400 px-2 py-0.5 font-medium uppercase tracking-wider">{r.preferred_vehicle_type}</span>}
                </div>
              </div>
              <div className="space-y-2.5">
                <div className="flex items-start gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 mt-1.5 shrink-0 ring-2 ring-emerald-400/20" />
                  <span className="text-sm text-white font-medium line-clamp-1">{r.pickup_location?.address}</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400 mt-1.5 shrink-0 ring-2 ring-red-400/20" />
                  <span className="text-sm text-white font-medium line-clamp-1">{r.dropoff_location?.address}</span>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><Icons.Users />{r.adults || 1}{r.children ? `+${r.children}` : ''}</span>
                  <span className="flex items-center gap-1"><Icons.Luggage />{r.luggage || 0}</span>
                  {r.distance_km && <span className="text-orange-400/80 font-medium">{r.distance_km} km</span>}
                </div>
                <div className="text-orange-400 text-xs font-bold uppercase tracking-wider flex items-center gap-1">Angebot <Icons.ChevronRight /></div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Request Detail + Offer ---
function RequestDetailScreen({ t, request, onBack, onOfferSent }) {
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/driver/profile');
        const v = r.data?.vehicles || [];
        setVehicles(v);
        if (v.length > 0) setVehicleId(v[0].id);
      } catch {}
    })();
  }, []);

  const formatDate = (d) => formatPickup(d);

  const handleSubmit = async () => {
    if (!price || parseFloat(price) <= 0) { setError(t('driver_offer_invalid_price')); return; }
    if (!agbAccepted) { setError('Bitte akzeptieren Sie die AGB.'); return; }
    setLoading(true);
    setError('');
    try {
      await api.post('/driver/offer', { transfer_id: request.id, vehicle_id: vehicleId, price: parseFloat(price), currency: 'EUR', notes });
      setSuccess(true);
      setTimeout(() => onOfferSent(), 1500);
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center py-20" data-testid="pwa-offer-success">
        <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
          <Icons.Check />
        </div>
        <h3 className="text-white text-xl font-bold mb-1">Angebot gesendet!</h3>
        <p className="text-slate-400 text-sm">{price} EUR</p>
      </div>
    );
  }

  return (
    <div className="pb-4" data-testid="pwa-request-detail">
      <button onClick={onBack} className="flex items-center gap-1.5 text-slate-400 mb-5 active:text-white py-2 text-sm font-medium" data-testid="pwa-back-btn"><Icons.ChevronLeft />{t('common_back')}</button>

      {/* Route Card */}
      <div className="bg-slate-900 border border-white/10 p-5 mb-4">
        <div className="text-xs text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-1.5"><Icons.Clock />{formatDate(request.pickup_date)}</div>
        <div className="flex gap-4">
          <div className="flex flex-col items-center pt-1">
            <div className="w-3.5 h-3.5 rounded-full bg-emerald-400 ring-4 ring-emerald-400/10" />
            <div className="w-0.5 flex-1 bg-gradient-to-b from-emerald-400/40 to-red-400/40 my-1" />
            <div className="w-3.5 h-3.5 rounded-full bg-red-400 ring-4 ring-red-400/10" />
          </div>
          <div className="flex-1 space-y-4">
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">{t('driver_col_from')}</div>
              <div className="text-sm text-white font-medium mt-0.5">{request.pickup_location?.address}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">{t('driver_col_to')}</div>
              <div className="text-sm text-white font-medium mt-0.5">{request.dropoff_location?.address}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2.5 mt-5 pt-4 border-t border-white/5">
          <span className="bg-slate-800 px-3 py-1.5 text-xs text-slate-300 flex items-center gap-1.5 font-medium"><Icons.Users />{request.adults || 1}{request.children ? ` + ${request.children} Kinder` : ''}</span>
          <span className="bg-slate-800 px-3 py-1.5 text-xs text-slate-300 flex items-center gap-1.5 font-medium"><Icons.Luggage />{request.luggage || 0} Gepäck</span>
          {request.distance_km && <span className="bg-orange-500/10 text-orange-400 px-3 py-1.5 text-xs font-bold">{request.distance_km} km</span>}
          {request.duration_minutes && <span className="bg-slate-800 px-3 py-1.5 text-xs text-slate-300">{request.duration_minutes} min</span>}
          {request.flight_number && <span className="bg-blue-500/10 text-blue-400 px-3 py-1.5 text-xs font-medium">{request.flight_number}</span>}
        </div>
        {request.notes && <div className="mt-3 text-xs text-slate-400 bg-slate-800/50 p-3 border-l-2 border-orange-500/30">{request.notes}</div>}
      </div>

      {/* Price Offer Form */}
      <div className="bg-slate-900 border border-white/10 p-5" data-testid="pwa-offer-form">
        <h3 className="text-white font-bold text-lg tracking-tight mb-4">{t('driver_make_offer_title')}</h3>
        {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm p-3 mb-4" data-testid="pwa-offer-error">{error}</div>}

        {vehicles.length > 1 && (
          <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}
            className="w-full bg-slate-950 border border-white/10 text-white px-4 py-3.5 mb-4 text-sm outline-none focus:border-orange-500">
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.make} {v.model} ({v.vehicle_type})</option>)}
          </select>
        )}

        {/* Price Input - Large fintech style */}
        <div className="flex items-center gap-3 bg-slate-950 border border-white/10 p-4 mb-4 focus-within:border-orange-500 transition-colors">
          <span className="text-slate-500 text-sm font-medium shrink-0">EUR</span>
          <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00"
            className="bg-transparent text-3xl font-bold text-white w-full outline-none placeholder:text-slate-700 tracking-tighter [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            data-testid="pwa-offer-price" />
        </div>

        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('driver_notes_placeholder')} rows={2}
          className="w-full bg-slate-950 border border-white/10 text-white px-4 py-3.5 mb-4 text-sm outline-none focus:border-orange-500 resize-none placeholder:text-slate-600" data-testid="pwa-offer-notes" />

        <label className="flex items-start gap-3 cursor-pointer mb-5 active:opacity-80" data-testid="pwa-offer-agb-label">
          <div className={`w-5 h-5 mt-0.5 border-2 flex items-center justify-center shrink-0 transition-colors ${agbAccepted ? 'bg-orange-500 border-orange-500' : 'border-white/20'}`}
            onClick={() => setAgbAccepted(!agbAccepted)}>
            {agbAccepted && <Icons.Check />}
          </div>
          <span className="text-xs text-slate-400 leading-relaxed">
            Ich akzeptiere die <a href="/fahrer-agb" target="_blank" rel="noopener noreferrer" className="text-orange-400 underline">Fahrer-AGB</a> und bestätige die Durchführung des Transfers.
          </span>
        </label>

        <button onClick={handleSubmit} disabled={loading || !agbAccepted || !price}
          className="w-full py-5 bg-orange-500 text-slate-950 font-bold text-lg active:scale-[0.98] transition-all disabled:opacity-40 flex items-center justify-center gap-2" data-testid="pwa-offer-submit">
          {loading ? <div className="w-5 h-5 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" /> : <><Icons.Euro />{t('driver_submit_offer')}</>}
        </button>
      </div>
    </div>
  );
}

// --- My Offers Screen ---
function OffersScreen({ t }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const r = await api.get('/driver/my-offers'); setOffers(r.data || []); }
      catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const formatDate = (d) => formatPickup(d);
  const statusColor = (s) => s === 'accepted' ? 'text-emerald-400 bg-emerald-500/10' : s === 'rejected' ? 'text-red-400 bg-red-500/10' : 'text-orange-400 bg-orange-500/10';

  if (loading) return <div className="text-center py-16 text-slate-400">{t('common_loading')}</div>;

  return (
    <div className="pb-4" data-testid="pwa-offers-screen">
      <h2 className="text-white font-semibold mb-4">{t('driver_my_offers')} ({offers.length})</h2>
      {offers.length === 0 ? (
        <div className="text-center py-16 text-slate-500">{t('driver_no_requests')}</div>
      ) : (
        <div className="space-y-3">
          {offers.map((o) => (
            <div key={o.id} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4" data-testid={`pwa-offer-${o.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-lg font-bold text-white">{o.price} EUR</span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(o.status)}`}>{o.status}</span>
              </div>
              {o.transfer && (
                <div className="space-y-1">
                  <div className="flex items-start gap-2"><div className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 shrink-0" /><span className="text-xs text-slate-300 line-clamp-1">{o.transfer.pickup_location?.address}</span></div>
                  <div className="flex items-start gap-2"><div className="w-2 h-2 rounded-full bg-red-400 mt-1.5 shrink-0" /><span className="text-xs text-slate-300 line-clamp-1">{o.transfer.dropoff_location?.address}</span></div>
                  <div className="text-xs text-slate-500 mt-1">{formatDate(o.transfer.pickup_date)}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Bookings Screen ---
function BookingsScreen({ t }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get('/driver/bookings'); setBookings(r.data || []); }
    catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleComplete = async (transferId) => {
    if (!window.confirm(t('driver_complete_confirm'))) return;
    setCompleting(transferId);
    try { await api.post(`/transfers/${transferId}/complete`); load(); }
    catch {}
    finally { setCompleting(null); }
  };

  const formatDate = (d) => { try { return new Date(d).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return d; } };

  if (loading) return <div className="text-center py-16 text-slate-400">{t('common_loading')}</div>;

  return (
    <div className="pb-4" data-testid="pwa-bookings-screen">
      <h2 className="text-white font-semibold mb-4">{t('driver_tab_bookings')} ({bookings.length})</h2>
      {bookings.length === 0 ? (
        <div className="text-center py-16 text-slate-500" data-testid="pwa-no-bookings">{t('driver_no_bookings')}</div>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => {
            const tr = b.transfer || {};
            const isCompleted = tr.status === 'completed';
            return (
              <div
                key={b.id}
                onClick={() => setSelected(b)}
                className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 active:bg-slate-800/80 cursor-pointer transition-colors"
                data-testid={`pwa-booking-${b.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-bold text-white">{b.price} EUR</span>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${isCompleted ? 'text-emerald-400 bg-emerald-500/10' : 'text-blue-400 bg-blue-500/10'}`}>
                    {isCompleted ? t('driver_status_completed') : t('driver_status_booked')}
                  </span>
                </div>
                <div className="space-y-1 mb-2">
                  <div className="flex items-start gap-2"><div className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 shrink-0" /><span className="text-xs text-slate-300 line-clamp-1">{tr.pickup_location?.address}</span></div>
                  <div className="flex items-start gap-2"><div className="w-2 h-2 rounded-full bg-red-400 mt-1.5 shrink-0" /><span className="text-xs text-slate-300 line-clamp-1">{tr.dropoff_location?.address}</span></div>
                </div>
                <div className="text-xs text-slate-500 mb-3">{formatDate(tr.pickup_date)}</div>
                {!isCompleted && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleComplete(tr.id); }}
                    disabled={completing === tr.id}
                    className="w-full bg-emerald-500/20 text-emerald-400 font-medium py-2.5 rounded-xl text-sm active:bg-emerald-500/30 transition-colors disabled:opacity-50"
                    data-testid={`pwa-complete-${b.id}`}
                  >
                    <span className="flex items-center justify-center gap-1.5"><Icons.Check />{completing === tr.id ? t('common_loading') : t('driver_complete_btn')}</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <BookingDetailModal
          booking={selected}
          onClose={() => setSelected(null)}
          t={t}
        />
      )}
    </div>
  );
}

// --- Profile Screen ---
function ProfileScreen({ t, driverProfile, userName, onLogout, language, setLanguage, onManageVehicles, onManageDocs }) {
  const [showLang, setShowLang] = useState(false);

  return (
    <div className="pb-4" data-testid="pwa-profile-screen">
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5 mb-4">
        <div className="flex items-center gap-4 mb-3">
          <div className="w-14 h-14 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400 font-bold text-xl">
            {userName?.[0] || 'F'}
          </div>
          <div>
            <h2 className="text-white font-semibold text-lg">{userName || 'Fahrer'}</h2>
            <div className="flex items-center gap-1.5 text-sm text-slate-400">
              <Icons.Star /><span>{driverProfile?.rating?.toFixed(1) || '5.0'}</span>
              <span className="mx-1">|</span>
              <span>{driverProfile?.total_trips || 0} {t('profile_trips')}</span>
            </div>
          </div>
        </div>
        <div className="text-xs text-slate-500">
          <span className={`inline-block px-2 py-0.5 rounded-full ${driverProfile?.status === 'approved' ? 'bg-emerald-500/10 text-emerald-400' : driverProfile?.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-red-500/10 text-red-400'}`}>
            {driverProfile?.status || 'pending'}
          </span>
          {driverProfile?.region && <span className="ml-2">{driverProfile.region}</span>}
        </div>
      </div>

      {/* Vehicles Summary */}
      {driverProfile?.vehicles?.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-400 text-xs uppercase tracking-wider">{t('profile_fleet')} ({driverProfile.vehicles.length})</h3>
          </div>
          {driverProfile.vehicles.map((v, i) => (
            <div key={i} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3 mb-2 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center text-slate-400"><Icons.Car /></div>
              <div className="flex-1">
                <div className="text-sm text-white font-medium">{v.make} {v.model}</div>
                <div className="text-xs text-slate-500">{v.vehicle_type} | {v.passengers} {t('common_persons')} | {v.plate_number}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${v.status === 'approved' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-yellow-500/10 text-yellow-400'}`}>{v.status}</span>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {/* Manage Vehicles */}
        <button onClick={onManageVehicles} className="w-full flex items-center gap-3 bg-slate-800/60 border border-slate-700/50 rounded-xl p-3.5 active:bg-slate-800" data-testid="pwa-profile-vehicles">
          <span className="text-orange-400"><Icons.Car /></span>
          <span className="text-sm text-white flex-1 text-left">{t('profile_fleet')}</span>
          <span className="text-xs text-slate-400">{driverProfile?.vehicles?.length || 0}/4</span><Icons.ChevronRight />
        </button>

        {/* Manage Documents */}
        <button onClick={onManageDocs} className="w-full flex items-center gap-3 bg-slate-800/60 border border-slate-700/50 rounded-xl p-3.5 active:bg-slate-800" data-testid="pwa-profile-docs">
          <span className="text-orange-400"><Icons.Upload /></span>
          <span className="text-sm text-white flex-1 text-left">{t('driver_docs_btn')}</span>
          <Icons.ChevronRight />
        </button>

        {/* Language */}
        <div className="relative">
          <button onClick={() => setShowLang(!showLang)} className="w-full flex items-center gap-3 bg-slate-800/60 border border-slate-700/50 rounded-xl p-3.5 active:bg-slate-800" data-testid="pwa-profile-lang">
            <Icons.Globe /><span className="text-sm text-white flex-1 text-left">{t('common_language')}</span>
            <span className="text-xs text-slate-400">{LANGUAGE_OPTIONS.find(l => l.code === language)?.name}</span><Icons.ChevronRight />
          </button>
          {showLang && (
            <div className="mt-1 bg-slate-800 rounded-xl border border-slate-700 py-1 max-h-52 overflow-y-auto">
              {LANGUAGE_OPTIONS.map((l) => (
                <button key={l.code} onClick={() => { setLanguage(l.code); setShowLang(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm ${language === l.code ? 'text-orange-400 bg-slate-700' : 'text-slate-300 active:bg-slate-700'}`}>
                  {l.flag} {l.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <a href="/" className="flex items-center gap-3 bg-slate-800/60 border border-slate-700/50 rounded-xl p-3.5 active:bg-slate-800">
          <Icons.Globe /><span className="text-sm text-white flex-1">{t('common_home')} (Webportal)</span><Icons.ChevronRight />
        </a>

        <button onClick={onLogout} className="w-full flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-3.5 active:bg-red-500/20" data-testid="pwa-logout-btn">
          <span className="text-red-400"><Icons.LogOut /></span><span className="text-sm text-red-400 flex-1 text-left">{t('nav_logout')}</span>
        </button>
      </div>

      <p className="text-center text-slate-600 text-xs mt-6">366Transfer Driver App v1.0</p>
    </div>
  );
}

// --- Main PWA App ---
export default function DriverApp() {
  const { user, token, login, logout, loadFromStorage, language, setLanguage } = useAuthStore();
  const { t } = useTranslation(language);
  const [tab, setTab] = useState('dashboard');
  const [driverProfile, setDriverProfile] = useState(null);
  const [driverProfileLoading, setDriverProfileLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [authMode, setAuthMode] = useState('login'); // login | register
  const [subScreen, setSubScreen] = useState(null); // null | 'vehicles' | 'documents' | 'setup'
  const { canInstall, install } = useInstallPrompt();

  useEffect(() => { loadFromStorage(); }, [loadFromStorage]);

  const refreshProfile = useCallback(() => {
    if (token) {
      setDriverProfileLoading(true);
      api.get('/driver/profile').then(r => { setDriverProfile(r.data); setDriverProfileLoading(false); }).catch(() => { setDriverProfile(null); setDriverProfileLoading(false); });
    }
  }, [token]);

  useEffect(() => { refreshProfile(); }, [refreshProfile]);

  // Push notification setup
  const notificationSetupDone = useRef(false);
  const [pushToast, setPushToast] = useState(null);
  const [notifStatus, setNotifStatus] = useState('idle'); // idle | subscribing | subscribed | denied | error
  const [refreshTrigger, setRefreshTrigger] = useState(0); // increment to trigger requests refresh

  useEffect(() => {
    if (!token || notificationSetupDone.current) return;
    notificationSetupDone.current = true;

    (async () => {
      try {
        setNotifStatus('subscribing');
        const fcmToken = await requestNotificationPermission();
        if (fcmToken) {
          await api.post('/notifications/subscribe', { token: fcmToken });
          setNotifStatus('subscribed');
          console.log('Push notifications subscribed successfully');
        } else {
          // Check if permission was denied
          if ('Notification' in window && Notification.permission === 'denied') {
            setNotifStatus('denied');
          } else {
            setNotifStatus('error');
          }
        }
      } catch (err) {
        console.error('Push notification setup failed:', err);
        setNotifStatus('error');
      }
    })();
  }, [token]);

  // Foreground message listener
  useEffect(() => {
    if (!token) return;
    const unsubscribe = onForegroundMessage((payload) => {
      const title = payload.notification?.title || 'Benachrichtigung';
      const body = payload.notification?.body || '';
      setPushToast({ title, body });
      setTimeout(() => setPushToast(null), 5000);
      // Play notification sound
      playNotificationSound();
      // Trigger refresh on any screen
      setRefreshTrigger(prev => prev + 1);
      refreshProfile();
    });
    return unsubscribe;
  }, [token, refreshProfile]);

  // Auto-polling: check for new requests every 30 seconds
  const lastRequestCount = useRef(null);
  useEffect(() => {
    if (!token || !driverProfile) return;
    const interval = setInterval(async () => {
      try {
        const r = await api.get('/driver/requests');
        const newCount = r.data?.length || 0;
        if (lastRequestCount.current !== null && newCount > lastRequestCount.current) {
          // New request detected via polling
          playNotificationSound();
          setRefreshTrigger(prev => prev + 1);
          refreshProfile();
          setPushToast({
            title: 'Neue Transferanfrage',
            body: `${newCount - lastRequestCount.current} neue Anfrage(n) verfügbar`,
          });
          setTimeout(() => setPushToast(null), 5000);
        }
        lastRequestCount.current = newCount;
      } catch {}
    }, 30000);
    return () => clearInterval(interval);
  }, [token, driverProfile, refreshProfile]);

  useEffect(() => {
    // Only register the caching service worker for PWA install (no Firebase here)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/driver-sw.js', { scope: '/driver-app' }).catch(() => {});
    }
  }, []);

  const handleLogin = async (email, password) => {
    return await login(email, password);
  };

  const handleLogout = () => { logout(); setDriverProfile(null); setTab('dashboard'); setSubScreen(null); setAuthMode('login'); };

  // Not logged in: show login or register
  if (!token) {
    if (authMode === 'register') {
      return <RegisterScreen t={t} Icons={Icons} onSwitchToLogin={() => setAuthMode('login')} language={language} setLanguage={setLanguage} />;
    }
    return <LoginScreen t={t} onLogin={handleLogin} onSwitchToRegister={() => setAuthMode('register')} language={language} setLanguage={setLanguage} />;
  }

  // Loading driver profile
  if (driverProfileLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="text-3xl font-black text-orange-500 mb-2">366</div>
          <p className="text-slate-400 text-sm">{t('common_loading')}</p>
        </div>
      </div>
    );
  }

  // Logged in but no driver profile: show setup wizard
  if (!driverProfile) {
    return (
      <>
        <Helmet><meta name="theme-color" content="#0f172a" /></Helmet>
        <div className="min-h-screen bg-slate-950 text-white flex flex-col max-w-md mx-auto">
          <div className="sticky top-0 z-30 bg-slate-950/95 backdrop-blur-sm border-b border-slate-800 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xl font-black text-orange-500">366</span>
              <span className="text-xs font-semibold text-slate-400 tracking-widest">DRIVER</span>
            </div>
            <button onClick={handleLogout} className="text-slate-400 text-xs">{t('nav_logout')}</button>
          </div>
          <div className="flex-1 px-4 pt-4 pb-8">
            <DriverSetupScreen t={t} Icons={Icons} onComplete={refreshProfile} />
          </div>
        </div>
      </>
    );
  }

  // Sub-screens (vehicles, documents)
  if (subScreen === 'vehicles') {
    return (
      <>
        <Helmet><meta name="theme-color" content="#0f172a" /></Helmet>
        <div className="min-h-screen bg-slate-950 text-white flex flex-col max-w-md mx-auto">
          <div className="sticky top-0 z-30 bg-slate-950/95 backdrop-blur-sm border-b border-slate-800 px-4 py-3 flex items-center gap-2">
            <span className="text-xl font-black text-orange-500">366</span>
            <span className="text-xs font-semibold text-slate-400 tracking-widest">DRIVER</span>
          </div>
          <div className="flex-1 px-4 pt-4 pb-8">
            <VehicleManageScreen t={t} Icons={Icons} driverProfile={driverProfile} onBack={() => setSubScreen(null)} onRefresh={refreshProfile} />
          </div>
        </div>
      </>
    );
  }

  if (subScreen === 'documents') {
    return (
      <>
        <Helmet><meta name="theme-color" content="#0f172a" /></Helmet>
        <div className="min-h-screen bg-slate-950 text-white flex flex-col max-w-md mx-auto">
          <div className="sticky top-0 z-30 bg-slate-950/95 backdrop-blur-sm border-b border-slate-800 px-4 py-3 flex items-center gap-2">
            <span className="text-xl font-black text-orange-500">366</span>
            <span className="text-xs font-semibold text-slate-400 tracking-widest">DRIVER</span>
          </div>
          <div className="flex-1 px-4 pt-4 pb-8">
            <DocumentsScreen t={t} Icons={Icons} driverProfile={driverProfile} onBack={() => setSubScreen(null)} onRefresh={refreshProfile} />
          </div>
        </div>
      </>
    );
  }

  const tabs = [
    { id: 'dashboard', icon: Icons.Home, label: t('nav_driver_dashboard').split(' ')[0] || 'Home' },
    { id: 'requests', icon: Icons.List, label: t('driver_tab_new') },
    { id: 'offers', icon: Icons.Tag, label: t('driver_my_offers').split(' ').pop() || 'Offers' },
    { id: 'bookings', icon: Icons.Car, label: t('driver_tab_bookings') },
    { id: 'profile', icon: Icons.User, label: t('profile_driver').split(' ')[0] || 'Profil' },
  ];

  const handleRetryNotif = async () => {
    setNotifStatus('subscribing');
    notificationSetupDone.current = false;
    try {
      const fcmToken = await requestNotificationPermission();
      if (fcmToken) {
        await api.post('/notifications/subscribe', { token: fcmToken });
        setNotifStatus('subscribed');
      } else {
        if ('Notification' in window && Notification.permission === 'denied') {
          setNotifStatus('denied');
        } else {
          setNotifStatus('error');
        }
      }
    } catch {
      setNotifStatus('error');
    }
  };

  const renderScreen = () => {
    if (selectedRequest) {
      return <RequestDetailScreen t={t} request={selectedRequest} onBack={() => setSelectedRequest(null)} onOfferSent={() => { setSelectedRequest(null); setTab('offers'); }} />;
    }
    switch (tab) {
      case 'requests': return <RequestsScreen t={t} onSelectRequest={setSelectedRequest} refreshTrigger={refreshTrigger} />;
      case 'offers': return <OffersScreen t={t} />;
      case 'bookings': return <BookingsScreen t={t} />;
      case 'profile': return <ProfileScreen t={t} driverProfile={driverProfile} userName={user?.name} onLogout={handleLogout} language={language} setLanguage={setLanguage} onManageVehicles={() => setSubScreen('vehicles')} onManageDocs={() => setSubScreen('documents')} />;
      default: return <DashboardScreen t={t} driverProfile={driverProfile} userName={user?.name} onRefresh={refreshProfile} onNavigate={(t) => { setTab(t); }} notifStatus={notifStatus} onRetryNotif={handleRetryNotif} />;
    }
  };

  return (
    <>
      <Helmet>
        <link rel="manifest" href="/driver-manifest.json" />
        <meta name="theme-color" content="#0f172a" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="366Driver" />
      </Helmet>
      <div className="min-h-screen bg-slate-950 text-white flex flex-col max-w-md mx-auto" data-testid="pwa-driver-app">
        {/* Header */}
        <div className="sticky top-0 z-30 bg-slate-950/95 backdrop-blur-sm border-b border-slate-800 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl font-black text-orange-500">366</span>
            <span className="text-xs font-semibold text-slate-400 tracking-widest">DRIVER</span>
          </div>
          {canInstall && (
            <button onClick={install} className="flex items-center gap-1 text-xs text-orange-400 bg-orange-500/10 px-3 py-1.5 rounded-full active:bg-orange-500/20" data-testid="pwa-install-btn">
              <Icons.Download />{t('common_add')}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pt-4 pb-24">
          {/* Push notification toast */}
          {pushToast && (
            <div className="fixed top-16 left-2 right-2 z-50 max-w-md mx-auto animate-in slide-in-from-top" data-testid="pwa-push-toast">
              <div className="bg-orange-500/95 backdrop-blur-sm rounded-xl p-3 shadow-xl border border-orange-400/30"
                onClick={() => { setPushToast(null); setTab('requests'); setSelectedRequest(null); }}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0 mt-0.5">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-white"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white">{pushToast.title}</div>
                    <div className="text-xs text-white/80 mt-0.5 line-clamp-2">{pushToast.body}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); setPushToast(null); }} className="text-white/60 hover:text-white p-1">
                    <Icons.X />
                  </button>
                </div>
              </div>
            </div>
          )}
          {renderScreen()}
        </div>

        {/* Bottom Navigation */}
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-slate-900/95 backdrop-blur-sm border-t border-slate-800 max-w-md mx-auto" data-testid="pwa-bottom-nav">
          <div className="flex">
            {tabs.map((item) => (
              <button key={item.id} onClick={() => { setTab(item.id); setSelectedRequest(null); }}
                className={`flex-1 flex flex-col items-center py-2.5 transition-colors ${tab === item.id ? 'text-orange-400' : 'text-slate-500 active:text-slate-300'}`} data-testid={`pwa-tab-${item.id}`}>
                <item.icon />
                <span className="text-[10px] mt-0.5 font-medium">{item.label}</span>
              </button>
            ))}
          </div>
          <div className="h-safe-area-bottom bg-slate-900" />
        </div>
      </div>
    </>
  );
}
